import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/I18nContext";
import client from "@/lib/api";
import { MapPin, Loader2, LocateFixed, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";

const localizedValue = (obj) => (obj && (obj["de-DE"] || Object.values(obj)[0])) || "";

function buildAddress(branch) {
  const b = branch || {};
  return `${localizedValue(b.street)}, ${b.zip_code || ""} ${localizedValue(b.city)}`.trim();
}

export function MapPickerDialog({ open, onOpenChange, branch, dealer, onSave }) {
  const { t } = useI18n();
  const [lat, setLat] = useState(branch?.latitude ?? null);
  const [lng, setLng] = useState(branch?.longitude ?? null);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [search, setSearch] = useState("");
  const mapEl = useRef(null);
  const searchInputRef = useRef(null);

  const { apiKey, loading, mapReady, mapError, applyLatLng } = useGoogleMaps({
    open,
    branch,
    mapElRef: mapEl,
    searchInputRef,
    onMarkerMoved: ({ lat: nlat, lng: nlng }) => {
      setLat(nlat);
      setLng(nlng);
    },
  });

  useEffect(() => {
    if (!open) return;
    setLat(branch?.latitude ?? null);
    setLng(branch?.longitude ?? null);
    setSearch("");
  }, [open, branch]);

  const address = buildAddress(branch);
  const showFallback = !loading && (!apiKey || mapError);

  const applyResult = ({ latitude, longitude }) => {
    setLat(latitude);
    setLng(longitude);
    applyLatLng(latitude, longitude);
  };

  const geocode = async (addr) => {
    const { data } = await client.post(`/geocode`, { address: addr });
    if (data.status !== "OK") throw new Error("No results");
    return data;
  };

  const locateFromAddress = () => {
    setSearch(address);
    searchInputRef.current?.focus();
  };

  const geocodeSearchTerm = async () => {
    const q = search.trim();
    if (!q) return;
    setLocating(true);
    try {
      const data = await geocode(q);
      applyResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No results for that search");
    } finally {
      setLocating(false);
    }
  };

  const doSave = async () => {
    if (lat == null || lng == null) return;
    setSaving(true);
    try {
      await onSave({ latitude: Number(lat), longitude: Number(lng) });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="map-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {t("dealership.coordinatesEdit")}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="data-label mb-1">Dealer</p>
              <p className="text-sm font-medium">{dealer?.name}</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{address}</p>
            </div>
            <Button data-testid="map-picker-locate" variant="outline" size="sm" className="gap-1.5"
              disabled={!address} onClick={locateFromAddress}>
              <LocateFixed className="h-3.5 w-3.5" />
              Locate from address
            </Button>
          </div>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            data-testid="map-picker-search"
            placeholder="Search a place, address or landmark…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !mapReady) {
                e.preventDefault();
                geocodeSearchTerm();
              }
            }}
            className="pl-9"
          />
          {!mapReady && search && (
            <Button data-testid="map-picker-search-submit" variant="ghost" size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
              onClick={geocodeSearchTerm} disabled={locating}>
              Go
            </Button>
          )}
        </div>

        {loading && (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {!loading && apiKey && !mapError && (
          <div ref={mapEl} className="h-72 w-full overflow-hidden rounded-md border border-border" data-testid="map-picker-canvas" />
        )}

        {showFallback && (
          <MapFallback error={mapError} />
        )}

        <div className="grid grid-cols-2 gap-3">
          <LatLngInput label="Latitude" testId="map-picker-lat" value={lat} setValue={setLat} />
          <LatLngInput label="Longitude" testId="map-picker-lng" value={lng} setValue={setLng} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="map-picker-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={doSave} disabled={saving || lat == null || lng == null} data-testid="map-picker-save">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LatLngInput({ label, testId, value, setValue }) {
  return (
    <div>
      <label className="data-label mb-1 block">{label}</label>
      <Input
        data-testid={testId}
        type="number"
        step="0.000001"
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value === "" ? null : Number(e.target.value))}
        className="font-mono"
      />
    </div>
  );
}

function MapFallback({ error }) {
  const testId = error ? "map-picker-error" : "map-picker-no-key";
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground"
    >
      {error === "auth" ? (
        <>
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-destructive" strokeWidth={1.5} />
          <p className="mb-1 font-medium text-foreground">Google Maps couldn&apos;t load</p>
          <p className="text-xs">
            The configured API key was rejected (invalid, referrer-restricted, or Maps JS API not enabled).
            Ask a MHQ Power Admin to update it in <span className="font-mono">Settings</span>. Search + numeric editing still work.
          </p>
        </>
      ) : error === "network" ? (
        <>
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-destructive" strokeWidth={1.5} />
          <p className="mb-1 font-medium text-foreground">Maps SDK failed to load</p>
          <p className="text-xs">Check network connectivity. Search + numeric editing still work.</p>
        </>
      ) : (
        <>
          <MapPin className="mx-auto mb-2 h-8 w-8" strokeWidth={1.25} />
          <p className="mb-1 font-medium text-foreground">Google Maps not configured</p>
          <p className="text-xs">
            Ask a MHQ Power Admin to add the Google Maps API key in <span className="font-mono">Settings</span>.
            You can still update lat / long numerically below.
          </p>
        </>
      )}
    </div>
  );
}
